import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useModal,
} from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { ViewModelForm, useDataRefresh, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import messages from './UpdateMedicalPackagePopup_AX.messages';
import metadata from './UpdateMedicalPackagePopup_AX.metadata.json5';

function UpdateMedicalPackagePopup(props) {
    const [medicalPackageVM, setMedicalPackageVM] = useState({});
    const [availableValues, setAvailableValues] = useState([]);
    const translator = useContext(TranslatorContext);
    const { refreshData } = useDataRefresh();
    const { isComponentValid, onValidate } = useValidation('UpdateMedicalPackagePopup');
    const {
        showAlert
    } = useModal();
    const {
        isOpen,
        onReject,
        onResolve,
        onAfterClose,
        serviceRequestNumber,
        authHeader,
        viewModelService
    } = props;

    useEffect(() => {
        const medicalPackages = [];
        viewModelService.productMetadata.get('cc').types.getTypelist('AX_MedicalPackage').codes
            .forEach((typecode) => {
                medicalPackages.push(_.merge({}, typecode, {
                    id: typecode.code,
                    displayName: translator({
                        id: `typekey.AX_MedicalPackage.${typecode.code}`,
                        defaultMessage: 'Not found'
                    })
                }));
            });
        setAvailableValues(medicalPackages);
        setMedicalPackageVM(
            viewModelService.create(
                {
                    serviceRequestNumber: serviceRequestNumber
                },
                'cc',
                'edge.capabilities.servicerequest.dto.ServiceRequestActionDTO'
            )
        );
    // only execute once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSend = useCallback(async () => {
        const updateMedicalPackage = await ServiceRequestService.updateMedicalPackage(
            medicalPackageVM.value,
            authHeader
        ).catch(() => {
            showAlert({
                title: messages.error,
                message: messages.errorMessage,
                status: 'error',
                icon: 'mi-error-outline'
            });
        });
        onResolve(updateMedicalPackage);
    }, [medicalPackageVM, authHeader, onResolve]);

    const handleClose = useCallback(() => {
        onReject();
    }, [onReject]);

    const handleMedicalPackage = useCallback((option) => {
        _.set(medicalPackageVM, 'description', option);
        refreshData();
    }, [medicalPackageVM, refreshData]);

    const handleMedicalPackageVMChange = useCallback((newModel) => {
        setMedicalPackageVM(newModel);
        refreshData();
    }, [refreshData]);

    if (_.isEmpty(medicalPackageVM)) {
        return null;
    }

    const overrides = {
        axUpdateMedicalPackageDropdownSelect: {
            availableValues: availableValues
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: onValidate,
            handleMedicalPackage: handleMedicalPackage
        }
    };

    return (
        <ModalNext isOpen={isOpen} onRequestClose={handleClose} onAfterClose={onAfterClose}>
            <ModalHeader title={translator(messages.updateMedicalPackageTitle)} />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.componentContent.content}
                    model={medicalPackageVM}
                    onValidationChange={onValidate}
                    onModelChange={handleMedicalPackageVMChange}
                    overrideProps={overrides}
                    callbackMap={resolvers.resolveCallbackMap}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    type="outlined"
                    onClick={handleClose}
                >
                    {translator(messages.cancel)}
                </Button>
                <Button
                    onClick={handleSend}
                    disabled={!isComponentValid}
                >
                    {translator(messages.send)}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

UpdateMedicalPackagePopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onResolve: PropTypes.func.isRequired,
    onReject: PropTypes.func.isRequired,
    onAfterClose: PropTypes.func.isRequired,
    serviceRequestNumber: PropTypes.string.isRequired,
    authHeader: PropTypes.string,
    medicalPackageVM: PropTypes.shape({
        serviceRequestNumber: PropTypes.string
    })
};

UpdateMedicalPackagePopup.defaultProps = {
    medicalPackageVM: undefined,
    authHeader: undefined
};

export default UpdateMedicalPackagePopup;
